/**
 * Гость-зритель: неавторизованный посетитель смотрит уже начатый матч (и живой
 * список лобби) без аккаунта.
 *
 * Сервер опознает участников лобби по playerId из запроса, поэтому у гостя свой
 * id с префиксом guest_. Настоящий id так выглядеть не может (ObjectId — 24
 * hex-символа, боты — b07…/b0f…), и по префиксу сервер закрывает гостю все,
 * кроме просмотра: вход игроком, создание лобби, рейтинговый поиск, ходы,
 * реакции, выбор фракции, работу за ботов (см. server/index.js).
 *
 * Id живет только в памяти вкладки: в Redux auth его класть нельзя — там по
 * id == null меню, награды, приглашения и рекламные счетчики понимают, что
 * это гость (см. configs/guestProfile.js). И не в localStorage/sessionStorage:
 * общий на вкладки id выбивал бы одну вкладку другой (SESSION_SUPERSEDED), а
 * после перезагрузки зритель в матч и так не возвращается.
 */

export const GUEST_VIEWER_ID_PREFIX = 'guest_';
// Имя, под которым гость числится зрителем. Сервер ставит его сам и не берет
// из запроса: гость без аккаунта не выбирает себе ник
export const GUEST_VIEWER_NAME = 'Гость';

export const checkIfGuestViewerId = (id) => typeof id === 'string' && id.startsWith(GUEST_VIEWER_ID_PREFIX);

const createRandomSuffix = () => {
  // crypto есть и в браузере, и в Node 19+ (сервер гостевой id не создает, но модуль импортирует)
  const cryptoApi = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(12));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  let suffix = '';
  while (suffix.length < 24) {
    suffix += Math.floor(Math.random() * 16).toString(16);
  }
  return suffix;
};

let guestViewerId = null;

/**
 * Id гостя в этой вкладке: один на все экраны до перезагрузки страницы
 */
export const getGuestViewerId = () => {
  if (!guestViewerId) {
    guestViewerId = `${GUEST_VIEWER_ID_PREFIX}${createRandomSuffix()}`;
  }
  return guestViewerId;
};

/**
 * Под каким id и именем клиент ходит в сеть лобби: авторизованный — под своим
 * аккаунтом, гость — под id вкладки
 *
 * @param {Object} auth - state.private.auth
 * @returns {{id: string|null, name: string|null, isGuest: boolean}}
 */
export const getLobbyNetworkIdentity = (auth) => (
  auth?.isGuest
    ? {id: getGuestViewerId(), name: GUEST_VIEWER_NAME, isGuest: true}
    : {id: auth?.id ?? null, name: auth?.name ?? null, isGuest: false}
);

// Определенный отказ сервера пустить гостя смотреть: 404 — лобби уже нет (матч
// закончился, сервер перезапущен), 403 — матч завершается или лобби не для
// гостя. Прочие ошибки (кратковременный 5xx) отказом не считаются
export const GUEST_JOIN_REFUSAL_STATUSES = [403, 404];
export const GUEST_SPECTATE_UNAVAILABLE_MESSAGE = 'Матч уже недоступен для просмотра';

export const checkIfGuestJoinRefused = (status) => GUEST_JOIN_REFUSAL_STATUSES.includes(status);

/**
 * Лобби, в которое гость может войти зрителем: матч уже идет, лобби не
 * приватное и не промо-лобби с ИИ (туда входят только с настоящим входом в
 * аккаунт, даже зрителем). Проверяют и клиент (клик в списке), и сервер (join)
 * — у сервера есть еще признак идущей партии lobby.store, а гостя в ИИ-лобби он
 * не пускает своей сверкой JWT (handleAiPromoLobbyJoin в server/index.js)
 */
export const checkIfGuestCanSpectateLobby = (lobby) => Boolean(
  lobby && lobby.status === 'game_started' && !lobby.isPrivate && !lobby.isPrivateFake && !lobby.isAiMatch
);

/**
 * Что гостю-зрителю можно слать по сокету лобби. Список разрешающий, а не
 * запрещающий: новый тип сообщения гостю закрыт, пока его сюда не впишут.
 * Пульс, запрос полного состояния (отстал от матча) и выход — больше зрителю
 * без аккаунта ничего не нужно: ни ходов, ни реакций, ни выбора фракции
 */
export const GUEST_VIEWER_ALLOWED_SOCKET_MESSAGE_TYPES = ['PONG', 'REQUEST_STATE', 'LEAVE_LOBBY'];

export const checkIfSocketMessageAllowedForViewer = (viewerId, messageType) => (
  !checkIfGuestViewerId(viewerId) || GUEST_VIEWER_ALLOWED_SOCKET_MESSAGE_TYPES.includes(messageType)
);

// Только для тестов: следующая выдача создаст новый id
export const resetGuestViewerIdForTests = () => {
  guestViewerId = null;
};
